import { AppRoutes } from "@/shared/lib/routes"
import { useState } from "react"
import { ArrowLeft, Loader2, Mail } from "lucide-react"

import LegalShield from "@/assets/img/compatibility/legal-shield.svg"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { LegalLink } from "@/shared/components/legal-link"
import type { StepComponentProps } from "./step-contract"
import { ZodiacPair } from "./zodiac-pair"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Full-bleed email screen matching the compatibility design: a You+Partner
 * zodiac header (from the collected birth dates) blending into the page above the
 * email capture, with its own navbar and legal note. Owns its CTA (gated on a
 * valid email) and shows an optional submit loader (`step.submitDelayMs`) before
 * advancing.
 */
export function CompatibilityEmailStep({
  step,
  value,
  onChange,
  onAdvance,
  onBack,
  answers,
}: StepComponentProps) {
  const [submitting, setSubmitting] = useState(false)
  const [focused, setFocused] = useState(false)
  const [touched, setTouched] = useState(false)
  const email = typeof value === "string" ? value : ""
  const valid = EMAIL_RE.test(email)
  // Active = the field is engaged (focused or already has text) — drives the
  // icon colour + input border, per the design's focus state.
  const active = focused || email.length > 0
  // Surface the invalid-email error once the user has left a non-empty field.
  const showError = touched && email.length > 0 && !valid

  const submit = () => {
    if (!valid || submitting) return
    if (step.submitDelayMs) {
      setSubmitting(true)
      setTimeout(onAdvance, step.submitDelayMs)
    } else {
      onAdvance()
    }
  }

  return (
    <div className="flex flex-1 flex-col bg-dark-base text-purple-light">
      <div className="relative">
        <ZodiacPair
          userDate={typeof answers.dob === "string" ? answers.dob : undefined}
          partnerDate={typeof answers["partner-dob"] === "string" ? answers["partner-dob"] : undefined}
        />
        <div className="absolute inset-x-0 top-0 flex h-14 items-center px-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
          >
            <ArrowLeft className="size-6" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col px-4 pb-6">
        <div className="space-y-2 text-center">
          <h1 className="font-display text-2xl font-medium text-purple-light">{step.title}</h1>
          {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
        </div>

        <label
          className={cn(
            "mt-6 flex h-14 items-center gap-3 rounded-xl border bg-purple-light/10 px-4 transition-colors",
            showError ? "border-peach" : active ? "border-main-purple" : "border-transparent",
          )}
        >
          <Mail
            className={cn(
              "size-6 shrink-0 transition-colors",
              showError ? "text-peach" : active ? "text-main-purple" : "text-purple-light/40",
            )}
          />
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            placeholder={step.placeholder}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false)
              setTouched(true)
            }}
            className="h-full flex-1 bg-transparent text-base text-purple-light caret-main-purple outline-none placeholder:text-purple-light/40"
          />
        </label>
        {showError && <p className="mt-2 text-sm text-peach">Please enter a valid email</p>}

        <div className="mt-auto space-y-4 pt-6">
          {submitting ? (
            <div
              aria-busy="true"
              className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-purple-light/10 font-body text-base font-semibold text-purple-light/70"
            >
              <Loader2 className="size-6 animate-spin" />
              Continue
            </div>
          ) : (
            <Button variant="gradient" size="xl" disabled={!valid} onClick={submit}>
              Continue
            </Button>
          )}

          {step.legalNote && (
            <div className="flex flex-col gap-2 rounded-2xl bg-purple-light/10 p-4">
              <div className="flex items-center gap-3">
                <img src={LegalShield} alt="" className="size-5 shrink-0" />
                <p className="flex-1 text-xs text-purple-light/70">
                  Your personal data is safe with us. We'll use your email for updates, receipts, and
                  subscription details.
                </p>
              </div>
              <p className="pl-8 text-xs text-purple-light/70">
                We respect your privacy. By continuing, you agree to our{" "}
                <LegalLink to={AppRoutes.TERMS} className="text-purple-light underline">
                  Terms &amp; Conditions
                </LegalLink>{" "}
                and{" "}
                <LegalLink to={AppRoutes.PRIVACY} className="text-purple-light underline">
                  Privacy Policy
                </LegalLink>
                .
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
